import { Box, Card, CardBody, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import type { Moment } from "moment";
import { useTranslation } from "react-i18next";
import { FiClock } from "react-icons/fi";
import { PortalLifecycleStatus, type PortalSourcingEventDetailDto } from "../../../../autogen/bff-api";
import { momentLocaleFor } from "../../../../common/formatting/i18nFormatters";
import { formatTimeLeft, timeLeftUntil } from "../../../../common/formatting/timeLeft";
import { formatDeadline } from "../utils/displayFormatters";
import { deadlineLabelKey, effectiveDeadlineIso } from "../utils/effectiveDeadline";
import { useCurrentMoment } from "../utils/useCurrentMoment";

export const EventDeadlineHighlight = ({
  event,
  now,
}: {
  event: PortalSourcingEventDetailDto;
  now?: Moment;
}) => {
  const { t, i18n } = useTranslation();
  const locale = momentLocaleFor(i18n.language);
  // The page passes its own clock so the card and the countdown badge never drift apart; standalone
  // callers (the bid wizard) get their own ticking one.
  const ownClock = useCurrentMoment();
  const deadlineIso = effectiveDeadlineIso(event);

  // The live countdown only makes sense while the competition is open; other states are conveyed by the lifecycle banner.
  if (event.lifecycle.status !== PortalLifecycleStatus.Open || deadlineIso == null) return null;

  const timeLeft = timeLeftUntil(deadlineIso, now ?? ownClock);

  return (
    <Card variant="outline" rounded="xl" bg="smPrimarySofter" borderColor="smPrimarySoft">
      <CardBody px={{ base: "4", md: "6" }} py={{ base: "5", md: "5" }}>
        <Flex align="center" justify="space-between" gap="4">
          <Flex align="center" gap="4">
            <Flex
              width={{ base: "44px", md: "52px" }}
              height={{ base: "44px", md: "52px" }}
              rounded="full"
              bg="smBackground"
              align="center"
              justify="center"
              flexShrink="0"
            >
              <Icon as={FiClock} boxSize="6" color="smPrimary" />
            </Flex>
            <Stack spacing="0">
              <Text
                fontSize="xs"
                fontWeight="semibold"
                color="smPrimary"
                textTransform="uppercase"
                letterSpacing="wide"
              >
                {t(deadlineLabelKey(event))}
              </Text>
              <Text fontSize={{ base: "md", md: "lg" }} fontWeight="semibold" color="smText">
                {formatDeadline(deadlineIso, t("at"), locale)}
              </Text>
            </Stack>
          </Flex>

          <Box textAlign="right">
            {timeLeft.unit === "daysAndHours" ? (
              <>
                <Text fontSize={{ base: "4xl", md: "5xl" }} fontWeight="bold" color="smPrimary" lineHeight="1">
                  {timeLeft.days}
                </Text>
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="smPrimary"
                  textTransform="uppercase"
                  letterSpacing="wide"
                  mt="1"
                >
                  {t("days_left", { count: timeLeft.days })}
                </Text>
                {timeLeft.hours > 0 && (
                  <Text fontSize="xs" color="smMuted">
                    {t("+ {{hours}} h", { hours: timeLeft.hours })}
                  </Text>
                )}
              </>
            ) : (
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="bold"
                color="smPrimary"
                lineHeight="1.2"
              >
                {formatTimeLeft(timeLeft, t)}
              </Text>
            )}
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
};
